import React, { useState } from "react";
import { useIntl, useParams } from "umi";
import { getLanguage } from "@home/utils/common";
import { LANGUAGE_EN } from "@home/_config/config";
import Abnormal from "@home/components/Abnormal";
import SvgIcon from "@home/components/SvgIcon";
import CommonBubble from "@home/components/CommonBubble";
import { deleteAppendix } from "@home/services/studio";
import { Button, notification, PopConfirm } from 'tea-component';
import './index.scss';

interface IHighlightItem {
  appendix: string;
  id: number;
}

interface IHighlightsReport {
  gameInfo?: { [key: string]: string; };
  filter: { [key: string]: string; };
  error: boolean;
  loading: boolean;
  appendixList: IHighlightItem[];
  normalNoContentEn: string;
  normalNoContentCn: string;
  editPermission: boolean;
  setClickEditAppendix: (arg0: boolean) => void;
  setClickAddAppendix: (arg0: boolean) => void;
  setAppendixContent: (data: { id: number; appendix: string; }) => void;
  clickAddHighlight: boolean;
  fetchData: () => void;
}

export default function Appendix(props: IHighlightsReport) {
  const {
    appendixList,
    error,
    loading,
    editPermission,
    normalNoContentEn,
    normalNoContentCn,
    setClickEditAppendix,
    setClickAddAppendix,
    setAppendixContent,
    fetchData,
    gameInfo,
  } = props;

  const { formatMessage, locale } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const { studioId } = useParams() || ({} as any);

  const [delLoading, setDelLoading] = useState(false);

  const deleteData = async (id: number, close: () => void) => {
    setDelLoading(true);
    const params = {
      studio_code: studioId,
      unified_id: gameInfo?.edition_unified_id,
      id
    };

    try {
      await deleteAppendix(params);
      notification.success({
        description: formatMessage({ id: 'dbh_studio_funcom_delete_modal_success' }),
        duration: 1000,
      });
      fetchData();
    } catch (error) {
      notification.error({
        description: formatMessage({ id: 'dbh_studio_funcom_delete_modal_failed' }),
        duration: 1000,
      });
    }
    setDelLoading(false);
    close();
  };

  const handleEditClick = (id: number, appendix: string) => (e: React.SyntheticEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    setAppendixContent({ id, appendix });
    setClickEditAppendix(true);
  };

  let content;
  if (appendixList.length > 0) {
    content = appendixList?.map((item, index) => (
      <div key={index} className="dbh-appendix-crud-content">
        <div style={{ width: '100%' }}>
          <div className="dbh-appendix-crud-item-title">
            <span>{formatMessage({ id: 'dbh_studio_appendix' })}&nbsp;{index + 1}</span>
            {
              editPermission && (
                <div className="dbh-icon-container">
                  <CommonBubble content={getLanguage(isEN, 'Edit', '编辑')}>
                    <span style={{ marginRight: '22px' }}>
                      <SvgIcon
                        className="dbh-studio-appendix-edit-icon"
                        type="edit-icon"
                        onClick={handleEditClick(item?.id, item?.appendix)} />
                    </span>
                  </CommonBubble>
                  <CommonBubble content={getLanguage(isEN, 'Delete', '删除')}>
                    <PopConfirm
                      title={getLanguage(isEN, 'Confirm Deletion', '确认删除')}
                      message={getLanguage(isEN, 'Are you sure you want to delete this? This action cannot be undone.', '您确定要删除此内容吗？删除后将无法恢复，请谨慎操作。')}
                      className="dbh-studio-appendix-delete-popconfirm"
                      placement="left"
                      footer={close => (
                        <>
                          <Button
                            loading={delLoading}
                            onClick={() => {
                              deleteData(item?.id, close);
                            }}
                          >
                            {getLanguage(isEN, 'Confirm Delete', '确认删除')}
                          </Button>
                          <Button
                            onClick={() => {
                              close();
                            }}
                          >
                            {getLanguage(isEN, 'Cancel', '取消')}
                          </Button>
                        </>
                      )}
                    >
                      <span >
                        <SvgIcon
                          className='dbh-studio-appendix-delete-icon'
                          type="delete-icon" />
                      </span>
                    </PopConfirm>
                  </CommonBubble>
                </div>
              )
            }
          </div>
          <div className="dbh-appendix-crud-item-summary">
            <span className="dbh-appendix-crud-item-summary" dangerouslySetInnerHTML={{ __html: item?.appendix as string }}></span>
          </div>
        </div>
      </div>
    ));
  } else if (editPermission) {
    content = (
      <div className="dbh-appendix-no-content">
        {formatMessage({ id: 'dbh_studio_no_content' })},&nbsp;
        <span
          className="dbh-appendix-no-content-add-btn"
          onClick={() => setClickAddAppendix(true)}
        >
          {formatMessage({ id: 'dbh_studio_add__an_appendix' })}
        </span>
      </div>
    );
  } else {
    content = (
      <div className="dbh-appendix-no-content">
        {getLanguage(isEN, normalNoContentEn, normalNoContentCn)}
      </div>
    );
  }

  return (
    <Abnormal
      isLoading={loading}
      isError={error}
      classNames={'dbh-appendix-crud-abnormal'}
      component={<div className="dbh-appendix-crud">{content}</div>}
    />
  );
}
